import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { PageRibbon, ScoreBadge } from '../../components';
import { apiService } from '../../services';
import type { LowScoreRow } from '../../types';
import '../Dashboard/Dashboard.css';

const ListIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
    <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.72 3h16.92a2 2 0 0 0 1.72-3L13.71 3.86a2 2 0 0 0-3.42 0Z" />
  </svg>
);

export const LowScoreList: React.FC = () => {
  const [rows, setRows] = useState<LowScoreRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await apiService.getLowScores();
      if (response.success) setRows(response.data.engineers);
      else setError(response.message);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load the low-score list');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="dashboard">
      <main className="dashboard__content">
        <PageRibbon
          title="Low-score list"
          icon={<ListIcon />}
          subtitle="Engineers averaging below 5.0 over the last 7 days, with at least 5 scored prompts."
          actions={<span className="pill">Fixed window · last 7 days</span>}
        />
        <Link to="/analytics" className="pa-row-link">← Analytics</Link>
        {error && <p className="dashboard__error">{error}</p>}
        <div className="pa-card">
          {loading ? (
            <p className="dashboard__muted" style={{ padding: 16 }}>Loading…</p>
          ) : rows.length === 0 ? (
            <p className="dashboard__muted" style={{ padding: 16 }}>No engineers meet the low-score rule for the last 7 days.</p>
          ) : (
            <div className="pa-table-wrap">
              <table className="pa-table">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th className="pa-table__num">Avg overall score · 7 days</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row) => (
                    <tr key={row.name}>
                      <td>{row.name}</td>
                      <td className="pa-table__num"><ScoreBadge score={row.avg_overall} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
        <p className="dashboard__footnote">
          Lowest first. Rows do not open a prompt, and the window switch on other pages does not change this list. Coaching happens outside this product.
        </p>
      </main>
    </div>
  );
};

export default LowScoreList;
